import React from 'react';
import { motion } from "framer-motion";
import { User, Star } from "lucide-react";
import InterestCard from "./InterestCard";
import { TALENT_TYPES } from "../../../constants/talentTypes";
import { useLanguage } from "../../../context/LanguageContext";

interface Props {
  selectedInterests: string[];
  onToggle: (interest: string) => void;
  onContinue: () => void;
}

const INTEREST_META: Record<string, { titleKey: string; descriptionKey: string; icon: React.ReactElement }> = {
  "modeling":          { titleKey: "admin.userModal.talentTypeModeling",       descriptionKey: "auth.interestStep.modelingDescription",       icon: <User size={24} /> },
  "content-creation":  { titleKey: "admin.userModal.talentTypeContentCreator", descriptionKey: "auth.interestStep.contentCreationDescription", icon: <Star size={24} /> },
  "both":              { titleKey: "admin.userModal.talentTypeBoth",          descriptionKey: "auth.interestStep.bothDescription",           icon: <Star size={24} /> },
};

export default function InterestStep({ selectedInterests, onToggle, onContinue }: Props) {
  const { t } = useLanguage();
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-xl font-bold text-gray-800 mb-2">{t("auth.interestStep.title")}</h3>
        <p className="text-sm text-gray-500">{t("auth.interestStep.subtitle")}</p>
      </div>

      <div className="grid gap-4">
        {TALENT_TYPES.map(({ value }) => (
          <InterestCard
            key={value}
            title={t(INTEREST_META[value].titleKey)}
            description={t(INTEREST_META[value].descriptionKey)}
            icon={INTEREST_META[value].icon}
            selected={selectedInterests.includes(value)}
            onClick={() => onToggle(value)}
          />
        ))}
      </div>

      {selectedInterests.length === 0 && (
        <p className="text-sm text-red-500 text-center">{t("auth.interestStep.selectAtLeastOne")}</p>
      )}

      <button
        type="button"
        onClick={onContinue}
        disabled={selectedInterests.length === 0}
        className="w-full bg-[#b3d130] text-gray-900 py-3 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {t("auth.interestStep.continueButton")}
      </button>
    </motion.div>
  );
}
